import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Check, Star, Play, ArrowRight } from 'lucide-react';
import {
  DEFAULT_CONTENT, BrandIcon, FeaturesGrid, AIDemoSection,
  ComparisonSection, TestimonialsCTA, HubFooter,
} from '../components/homepage/HubSections';
import { HubHeader, useMarketingTheme } from '../components/homepage/MarketingShell';
import { HubPricing } from '../components/homepage/HubPricing';

const API = process.env.REACT_APP_BACKEND_URL;

// Ad-group-specific hero copy. Each variant reuses the entire homepage
// layout below the hero (features, AI demo, comparison, pricing,
// testimonials, CTA, footer) so the visitor still sees the full product
// story — only the hero label + headline + subtext + SEO change to match
// the ad keyword intent.
export const HERO_VARIANTS = {
  cpa: {
    label: 'BUILT FOR CPA FIRMS',
    headline1: 'Run Your CPA Firm',
    headline2: 'On One Platform.',
    headline3: 'Not Ten.',
    subtext: 'Client portal, secure document exchange, e-signatures, invoicing, and real-time books — every tool a modern CPA needs, without the SaaS sprawl.',
    seoTitle: 'CPA Firm Software — All-in-One Platform | CountBucks',
    seoDescription: 'Ditch the 8-app stack. CountBucks gives CPA firms client portal, docs, e-sign, invoicing, and books in one platform. Start free.',
    canonical: 'https://countbucks.com/home-cpa',
  },
  bookkeeping: {
    label: 'AI-POWERED BOOKKEEPING',
    headline1: 'Bookkeeping That',
    headline2: 'Practically Does',
    headline3: 'Itself.',
    subtext: 'Auto-categorize transactions, reconcile with one tap, and get monthly financials without lifting a finger. Priority support included.',
    seoTitle: 'AI Bookkeeping Software — Auto-Categorize & Reconcile | CountBucks',
    seoDescription: 'Automated bookkeeping powered by AI. Import bank transactions, auto-categorize, reconcile, and file with priority support. Try free for 7 days.',
    canonical: 'https://countbucks.com/home-bookkeeping',
  },
  quickbooks: {
    label: 'SWITCH FROM QUICKBOOKS',
    headline1: 'The Modern',
    headline2: 'QuickBooks',
    headline3: 'Alternative.',
    subtext: 'Everything QuickBooks does — plus AI bookkeeping, e-signatures, client portal, and priority support. Migrate in minutes, keep every transaction.',
    seoTitle: 'QuickBooks Alternative — Migrate in Minutes | CountBucks',
    seoDescription: 'Modern QuickBooks alternative with AI bookkeeping, e-signatures, client portal, and CPA support built-in. One-click migration from QuickBooks.',
    canonical: 'https://countbucks.com/home-quickbooks',
  },
};

const HERO_AVATARS = [
  '/avatars/owner1.jpg',
  '/avatars/owner2.jpg',
  '/avatars/owner3.jpg',
  '/avatars/owner4.jpg',
  '/avatars/owner5.jpg',
];

function useHomepageSeo(seo, canonicalHref) {
  useEffect(() => {
    if (!seo) return;
    document.title = seo.title;
    const ensure = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    ensure('name', 'description', seo.description);
    ensure('property', 'og:title', seo.title);
    ensure('property', 'og:description', seo.description);
    ensure('property', 'og:type', 'website');
    ensure('name', 'twitter:card', 'summary_large_image');
    ensure('name', 'twitter:title', seo.title);
    ensure('name', 'twitter:description', seo.description);
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalHref || 'https://countbucks.com/');
    let ld = document.getElementById('cb-home-jsonld');
    if (!ld) {
      ld = document.createElement('script');
      ld.type = 'application/ld+json';
      ld.id = 'cb-home-jsonld';
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify([
      { '@context': 'https://schema.org', '@type': 'Organization', name: 'CountBucks', url: 'https://countbucks.com', logo: 'https://countbucks.com/cb-logo.png' },
      { '@context': 'https://schema.org', '@type': 'SoftwareApplication', name: 'CountBucks', applicationCategory: 'BusinessApplication', operatingSystem: 'Web', offers: { '@type': 'Offer', price: '19.99', priceCurrency: 'USD' } },
    ]);
  }, [seo, canonicalHref]);
}

function HeroComparisonCard({ oldWay, smartWay }) {
  return (
    <div className="relative rounded-3xl border border-[#E4E8E6] bg-white p-4 shadow-xl shadow-black/5 sm:p-6 dark:border-[#1C2420] dark:bg-[#10150F]/60" data-testid="hero-comparison-card">
      <div className="grid gap-10 md:grid-cols-2 md:gap-8">
        {/* Old way */}
        <div data-testid="old-way-card">
          <p className="text-center text-sm font-bold tracking-wide text-[#18202D] dark:text-white">{oldWay.title}</p>
          <p className="mt-1 text-center text-xs text-[#596273] dark:text-[#8B948F]">{oldWay.subtitle}</p>
          <div className="mt-4 divide-y divide-[#F0F2F1] rounded-2xl border border-[#E4E8E6] bg-[#F7F9F8] px-4 dark:divide-[#1C2420] dark:border-[#1C2420] dark:bg-[#0D120E]">
            {oldWay.items.map((tool) => (
              <div key={tool.name} className="flex items-center gap-3 py-2.5" data-testid={`old-way-item-${tool.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <BrandIcon name={tool.name} slug={tool.slug} src={tool.src} color={tool.color} />
                <span className="flex-1 text-sm text-[#18202D] dark:text-[#D9DED9]">{tool.name}</span>
                <span className="text-xs font-medium text-[#596273] dark:text-[#8B948F]">{tool.price}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-between px-1">
            <span className="text-sm font-bold text-[#FF7816]">{oldWay.totalLabel}</span>
            <span className="text-2xl font-extrabold text-[#FF7816]" data-testid="old-way-total">{oldWay.total}</span>
          </div>
        </div>
        {/* Smart way */}
        <div data-testid="smart-way-card">
          <p className="text-center text-sm font-bold tracking-wide text-[#18202D] dark:text-white">{smartWay.title}</p>
          <p className="mt-1 text-center text-xs font-semibold text-[#118A35] dark:text-[#27B84A]">{smartWay.subtitle}</p>
          <div className="mt-4 rounded-2xl border border-[#D5EEDB] bg-[#F2FBF4] p-4 dark:border-[#1E3A26] dark:bg-[#0E1A11]">
            <div className="flex items-center justify-center gap-2 border-b border-[#D5EEDB] pb-3 dark:border-[#1E3A26]">
              <img src="/cb-logo.png" alt="CountBucks" className="h-8 w-8" />
              <span className="text-sm font-extrabold tracking-[0.15em] text-[#118A35] dark:text-[#27B84A]">COUNTBUCKS</span>
            </div>
            <ul className="mt-3 space-y-2">
              {smartWay.items.map((item) => (
                <li key={item} className="flex items-center gap-2.5 text-sm text-[#18202D] dark:text-[#D9DED9]">
                  <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#27B84A]">
                    <Check className="h-3 w-3 text-white" strokeWidth={3} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-4 flex items-center justify-between px-1">
            <span className="text-sm font-bold text-[#118A35] dark:text-[#27B84A]">{smartWay.totalLabel}</span>
            <span className="text-2xl font-extrabold text-[#118A35] dark:text-[#27B84A]" data-testid="smart-way-total">
              {smartWay.total}<span className="text-sm font-semibold">{smartWay.totalSuffix}</span>
            </span>
          </div>
        </div>
      </div>
      {/* Orange arrow */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 md:flex" aria-hidden="true">
        <motion.span
          animate={{ x: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: 'easeInOut' }}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-[#FF7816] shadow-lg shadow-orange-500/30 ring-4 ring-white dark:ring-[#10150F]"
        >
          <ArrowRight className="h-5 w-5 text-white" strokeWidth={3} />
        </motion.span>
      </div>
    </div>
  );
}

function CompareStrip({ strip }) {
  return (
    <section className="border-y border-[#E4E8E6] bg-[#F7F9F8] dark:border-[#1C2420] dark:bg-[#0D120E]" data-testid="compare-strip">
      <div className="mx-auto flex max-w-7xl flex-col items-center gap-4 px-4 py-6 sm:px-6 lg:flex-row lg:justify-between">
        <p className="text-sm font-semibold text-[#596273] dark:text-[#8B948F]">{strip.label}</p>
        <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3">
          {strip.items.map((item) => (
            <span key={item.name} className="flex items-center gap-2" data-testid={`compare-strip-${item.name.toLowerCase()}`}>
              <BrandIcon name={item.name} slug={item.slug} src={item.src} />
              <span className="text-sm font-bold text-[#596273] dark:text-[#AEB6B1]">{item.name}</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function LandingPageHub({ variant }) {
  const navigate = useNavigate();
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const { dk, toggleTheme } = useMarketingTheme();

  // Apply variant-specific hero copy + SEO. Falls back to the CMS/default
  // content when `variant` isn't set (i.e. plain "/" or "/home").
  const variantCfg = variant ? HERO_VARIANTS[variant] : null;
  const effectiveContent = useMemo(() => {
    if (!variantCfg) return content;
    return {
      ...content,
      hero: {
        ...content.hero,
        label: variantCfg.label,
        headline1: variantCfg.headline1,
        headline2: variantCfg.headline2,
        headline3: variantCfg.headline3,
        subtext: variantCfg.subtext,
      },
      seo: {
        ...(content.seo || {}),
        title: variantCfg.seoTitle,
        description: variantCfg.seoDescription,
      },
    };
  }, [content, variantCfg]);

  useHomepageSeo(effectiveContent.seo, variantCfg?.canonical);

  useEffect(() => {
    fetch(`${API}/api/public/homepage`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && d.hero) setContent({ ...DEFAULT_CONTENT, ...d }); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (location.hash) {
      const t = setTimeout(() => {
        document.querySelector(location.hash)?.scrollIntoView({ behavior: 'smooth' });
      }, 150);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  const onAnchor = (e, href) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      document.querySelector(href)?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
    } else if (href.startsWith('/')) {
      e.preventDefault();
      navigate(href);
    }
  };

  const fade = useMemo(() => (delay = 0) => (reduceMotion ? {} : {
    initial: { opacity: 0, y: 22 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-40px' },
    transition: { duration: 0.55, delay, ease: 'easeOut' },
  }), [reduceMotion]);

  const { hero } = effectiveContent;
  const vis = effectiveContent.sectionVisibility || {};
  const show = (key) => vis[key] !== false;

  // Guarantee Pricing + Book a Call nav entries even if CMS-stored nav
  // predates them.
  const navItems = useMemo(() => {
    const items = [...(effectiveContent.nav || [])];
    if (!items.some((n) => n.href === '#pricing')) items.push({ label: 'Pricing', href: '#pricing' });
    if (!items.some((n) => n.href === '/book')) items.push({ label: 'Book a Call', href: '/book' });
    return items;
  }, [effectiveContent.nav]);

  return (
    <div className={dk ? 'dark' : ''}>
      <div className="min-h-screen overflow-x-hidden bg-white font-sans text-[#18202D] antialiased dark:bg-[#0A0E0C] dark:text-[#F2F5F3]" data-testid="landing-hub-page">
        <HubHeader nav={navItems} dk={dk} onToggleTheme={toggleTheme} onAnchor={onAnchor} />

        {/* HERO */}
        <section className="mx-auto grid max-w-7xl items-center gap-12 px-4 pb-16 pt-12 sm:px-6 lg:grid-cols-2 lg:gap-10 lg:pt-16">
          <motion.div {...fade(0)}>
            <span className="inline-block rounded-full border border-[#27B84A]/40 bg-[#F2FBF4] px-4 py-1.5 text-xs font-bold tracking-widest text-[#118A35] dark:bg-[#0E1A11] dark:text-[#27B84A]" data-testid="hero-label">
              {hero.label}
            </span>
            <h1 className="mt-5 text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-headline">
              {hero.headline1}<br />{hero.headline2}<br />
              <span className="text-[#27B84A]">{hero.headline3}</span>
            </h1>
            <p className="mt-5 max-w-lg text-base text-[#596273] sm:text-lg dark:text-[#AEB6B1]" data-testid="hero-subtext">{hero.subtext}</p>
            <div className="mt-5 flex flex-wrap gap-3">
              {hero.pills.map((pill) => (
                <span key={pill} className="flex items-center gap-1.5 rounded-full border border-[#E4E8E6] bg-white px-3.5 py-1.5 text-sm font-semibold shadow-sm dark:border-[#1C2420] dark:bg-[#10150F]" data-testid={`hero-pill-${pill.toLowerCase().replace(/\s+/g, '-')}`}>
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#27B84A]"><Check className="h-2.5 w-2.5 text-white" strokeWidth={4} /></span>
                  {pill}
                </span>
              ))}
            </div>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Link
                to={hero.primaryCtaHref}
                data-testid="hero-cta-primary"
                className="inline-flex items-center justify-center rounded-2xl bg-[#27B84A] px-7 py-3.5 text-base font-bold text-white shadow-lg shadow-green-600/25 transition-all hover:-translate-y-0.5 hover:bg-[#118A35]"
              >
                {hero.primaryCta}
              </Link>
              <Link
                to={hero.secondaryCtaHref}
                data-testid="hero-cta-demo"
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-[#E4E8E6] bg-white px-7 py-3.5 text-base font-bold text-[#18202D] transition-colors hover:border-[#27B84A] dark:border-[#1C2420] dark:bg-[#10150F] dark:text-white"
              >
                <Play className="h-4 w-4 fill-current" /> {hero.secondaryCta}
              </Link>
            </div>
            <div className="mt-7 flex items-center gap-4" data-testid="hero-trust-line">
              <div className="flex -space-x-2.5">
                {HERO_AVATARS.map((src) => (
                  <img key={src} src={src} alt="" loading="lazy" className="h-9 w-9 rounded-full border-2 border-white object-cover dark:border-[#0A0E0C]" />
                ))}
              </div>
              <div>
                <div className="flex gap-0.5" aria-label="5 star rating">
                  {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-[#F59E0B] text-[#F59E0B]" />)}
                </div>
                <p className="mt-0.5 text-xs font-semibold text-[#596273] dark:text-[#8B948F]">{hero.trustLine}</p>
              </div>
            </div>
          </motion.div>
          <motion.div {...fade(0.15)}>
            <HeroComparisonCard oldWay={effectiveContent.oldWay} smartWay={effectiveContent.smartWay} />
          </motion.div>
        </section>

        {show('compareStrip') && <CompareStrip strip={effectiveContent.compareStrip} />}
        {show('features') && <FeaturesGrid content={effectiveContent} fade={fade} onAnchor={onAnchor} />}
        {show('ai') && <AIDemoSection ai={effectiveContent.aiSection} fade={fade} reduceMotion={reduceMotion} />}
        {show('comparison') && <ComparisonSection cmp={effectiveContent.comparison} fade={fade} />}
        {show('pricing') && <HubPricing fade={fade} />}
        {(show('testimonials') || show('finalCta')) && (
          <TestimonialsCTA
            testimonials={effectiveContent.testimonials}
            cta={effectiveContent.finalCta}
            fade={fade}
            showTestimonials={show('testimonials')}
            showCta={show('finalCta')}
          />
        )}
        <HubFooter footer={effectiveContent.footer} onAnchor={onAnchor} />
      </div>
    </div>
  );
}
